import React, { useState } from "react";
import { faTrash, faArrowRight, faNoteSticky, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionMenu from "../action-menu/ActionMenu";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import ListSelectionDialog from "../list-selection-dialog/ListSelectionDialog";
import { Note, Collection } from "../../types";
import { useTranslation } from "react-i18next";
import { formatNoteLastUpdated } from "../../utils/Utils";
import { getNotePlainText, noteContentToHtml, sanitizeNoteHtml } from "../../utils/noteFormatting";

import "./NoteElement.css";

interface NoteElementProps {
    element: Note;
    deleteElement: (id: string) => void;
    lists: Collection[];
    selectedListId: string;
    moveElementToList: (element: Note, listId: string) => void;
    onEdit: (note: Note) => void;
    showPreview?: boolean;
    onMoveToSection?: (element: Note) => void;
}

export const NoteElement: React.FC<NoteElementProps> = ({
    element,
    deleteElement,
    lists,
    selectedListId,
    moveElementToList,
    onEdit,
    showPreview = false,
    onMoveToSection,
}) => {
    const { t } = useTranslation();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const formattedContent = sanitizeNoteHtml(noteContentToHtml(element.content || ''));
    const hasContent = !!getNotePlainText(formattedContent).trim();
    const lastUpdatedLabel = element.lastUpdated
        ? formatNoteLastUpdated(element.lastUpdated, {
            now: t("noteElement.now"),
            yesterday: t("noteElement.yesterday"),
            minuteShort: t("noteElement.minuteShort"),
        })
        : "";

    const handleDelete = () => {
        deleteElement(element.id);
        setShowDeleteConfirmationDialog(false);
    };

    const [showListSelectionDialog, setShowListSelectionDialog] = useState(false);

    const actions = [
        {
            label: t('noteElement.moveToCollection'),
            icon: faArrowRight,
            callback: () => setShowListSelectionDialog(true),
        },
        ...(onMoveToSection ? [{
            label: t('sections.moveToSection'),
            icon: faLayerGroup,
            callback: () => onMoveToSection(element),
        }] : []),
        {
            label: t('noteElement.deleteNote'),
            icon: faTrash,
            callback: () => setShowDeleteConfirmationDialog(true),
        },
    ];

    return (
        <div className="note-element " onClick={() => onEdit(element)}>
            <div className="note-header">
                <div className="note-heading">
                    <span className="element-kind-icon note-kind-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faNoteSticky} />
                    </span>
                    <div className="note-title-block">
                        <span className="element-type-label note-type-label">{t('elementTypes.note')}</span>
                        <div className="note-title">
                            {element.title || t("noteElement.untitledNote")}
                        </div>
                    </div>
                </div>
                <div className="note-header-actions" onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                        actions={actions}
                        icon={faEllipsisV}
                        buttonStyle={{}}
                        buttonClassName="note-menu-button"
                    />
                </div>
            </div>
            <div className="note-content multi-line-text">
                {hasContent ? (
                    <span dangerouslySetInnerHTML={{ __html: formattedContent }} />
                ) : (
                    <em>{t("noteElement.noContent")}</em>
                )}
                {showPreview && lastUpdatedLabel && (
                    <span className="note-inline-updated">
                        {lastUpdatedLabel}
                    </span>
                )}
            </div>
            {element.lastUpdated && (
                <div className="note-meta">
                    <span className="note-last-updated">
                        {lastUpdatedLabel}
                    </span>
                </div>
            )}
            {showDeleteConfirmationDialog && (
                <ConfirmationDialog
                    title={t("noteElement.deleteNote")}
                    message={t("noteElement.deleteNoteConfirm")}
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteConfirmationDialog(false)}
                    isDanger={true}
                />
            )}
            {showListSelectionDialog && (
                <ListSelectionDialog
                    lists={lists}
                    selectedListId={selectedListId}
                    onSelectList={(listId: string) => {
                        moveElementToList(element, listId);
                        setShowListSelectionDialog(false);
                    }}
                    onClose={() => setShowListSelectionDialog(false)}
                />
            )}
        </div>
    );
};
